import { isRubricTask } from './rubricAnnotations';

export function questionAnchorKey(value) {
  return String(value || '').trim().toLowerCase().replace(/^(?:question|vraag|q)\s*/i, '')
    .replace(/\(([a-z0-9]+)\)/g, '.$1').replace(/\s+/g, '')
    .replace(/(\d)([a-z])$/, '$1.$2').replace(/[.:)]+$/, '');
}

function runRect(item, start, length) {
  const width = item.right - item.x;
  return { x: item.x + width * start / item.str.length, right: item.x + width * (start + length) / item.str.length,
    cy: item.cy, h: item.h };
}

/** Index ALL question headers, so an ungraded intervening question still bounds a region. */
export function indexPrintedMarkAnchors(pages) {
  const index = new Map(); const brackets = [];
  for (const page of pages) {
    const lines = [];
    for (const item of page.items) {
      let line = lines.find(row => Math.abs(row.cy - item.cy) <= Math.max(row.h, item.h) * .45);
      if (!line) { line = { cy: item.cy, h: item.h, items: [] }; lines.push(line); }
      line.items.push(item);
    }
    lines.sort((a, b) => a.cy - b.cy);
    const labels = [];
    let numberedSummary = '';
    for (const line of lines) {
      line.items.sort((a, b) => a.x - b.x);
      let offset = 0;
      const segments = line.items.map(item => { const segment = { item, start: offset, end: offset + item.str.length }; offset = segment.end + 1; return segment; });
      const joined = line.items.map(item => item.str).join(' ');
      const sectionHeading = joined.match(/^\s*(?:question|vraag)\s*(\d+)\b/i);
      if (sectionHeading) numberedSummary = /opsomming|summary/i.test(joined) ? sectionHeading[1] : '';
      for (const match of joined.matchAll(/\(\s*\d+(?:\.\d+)?\s*\)|\[\s*\d+(?:\.\d+)?\s*\]/g)) {
        const parts = segments.filter(segment => segment.end > match.index && segment.start < match.index + match[0].length);
        const rects = parts.map(segment => runRect(segment.item, Math.max(0, match.index - segment.start),
          Math.min(segment.end, match.index + match[0].length) - Math.max(segment.start, match.index)));
        const rect = { x: rects[0].x, right: rects[rects.length - 1].right, cy: line.cy, h: line.h };
        if (rect.x >= .5) brackets.push({ ...rect, page: page.page, value: Number(match[0].replace(/[^\d.]/g, '')), width: page.width, height: page.height });
      }
      const consumed = new Set();
      const summaryPrefix = numberedSummary;
      line.items.forEach((first, start) => {
        if (consumed.has(start) || first.x > .85) return;
        // Ordering summaries often print five cells as "1. 2. 3. 4. 5."
        // beneath Vraag 3, while the scoring identifiers are 3.1 ... 3.5.
        const localNumbers = summaryPrefix ? [...first.str.matchAll(/(?:^|\s)([1-5])[.)](?=\s|$)/g)] : [];
        if (localNumbers.length) {
          localNumbers.forEach(match => labels.push({ ...runRect(first, match.index + match[0].indexOf(match[1]), match[1].length + 1),
            key: `${summaryPrefix}.${match[1]}`, tableCell: true, page: page.page, width: page.width, height: page.height }));
          return;
        }
        // Keep separate columns separate, but rejoin split numeric label runs.
        let text = first.str;
        for (let n = start + 1; n < Math.min(start + 4, line.items.length); n++) {
          const prior = line.items[n - 1]; const next = line.items[n];
          if ((next.x - prior.right) * page.width > 7) break;
          const numericContinuation = (/\.\s*$/.test(text) && /^\d/.test(next.str)) || (/\d$/.test(text) && /^\./.test(next.str));
          const childContinuation = /^\d+(?:\.\d+)*$/.test(text) && /^\([a-z]\)$/i.test(next.str);
          if (!numericContinuation && !childContinuation) break;
          text += numericContinuation ? next.str : ` ${next.str}`; consumed.add(n);
        }
        const numbered = text.match(/^(?:(?:question|vraag)\s*)?(\d+(?:\s*\.\s*\d+)*(?:\s*\([a-z]\)|[a-z])?)(?:[.:)](?=\s|$))?(?=\s|$)/i);
        const letter = text.match(/^\(?([a-z])\)[.\s]*(?=\s|$|[A-Z])/);
        const parent = labels.filter(label => !/\.[a-z]$/.test(label.key) && Math.abs(label.x - first.x) < .2)
          .sort((a, b) => (line.cy - a.cy + Math.abs(a.x - first.x) * 2) - (line.cy - b.cy + Math.abs(b.x - first.x) * 2))[0];
        const key = numbered ? questionAnchorKey(numbered[1]) : letter && parent ? `${parent.key}.${letter[1]}` : '';
        if (!key) return;
        labels.push({ ...runRect(first, 0, Math.min(first.str.length, (numbered?.[0] || letter?.[0] || '').length)), key,
          page: page.page, width: page.width, height: page.height });
      });
    }
    labels.forEach((label, position) => {
      const next = labels.slice(position + 1).find(other => other.cy > label.cy + label.h * .5 && Math.abs(other.x - label.x) < .2);
      const end = Math.min(next ? next.cy - Math.min(label.h, next.h) * .5 : .96, label.tableCell ? label.cy + .04 : .96);
      const columnEdge = Math.min(1.1, ...labels.filter(other => other.x > label.x + .22).map(other => other.x));
      const nearby = brackets.filter(bracket => bracket.page === page.page
        && bracket.x > label.x && bracket.x < columnEdge && bracket.cy >= label.cy - label.h * .55 && bracket.cy < end);
      // Same-line brackets are strongest; otherwise use the last bracket within
      // this exact question region, never the first generic (1) elsewhere on a page.
      const sameLine = nearby.filter(bracket => Math.abs(bracket.cy - label.cy) <= label.h * .7);
      const bracket = sameLine.sort((a, b) => b.x - a.x)[0] || nearby[nearby.length - 1];
      if (!index.has(label.key)) index.set(label.key, []);
      index.get(label.key).push({ ...label, bracket, regionEnd: end });
    });
  }
  return { index, brackets };
}

export function questionPaperPage(annotations, questionNumber, pageCount = Infinity) {
  const key = questionAnchorKey(questionNumber);
  const candidates = (annotations || []).filter(annotation => questionAnchorKey(annotation.questionNumber) === key
    && !annotation.needsPlacement && Number(annotation.page) >= 1 && Number(annotation.page) <= pageCount
    && (annotation.tickPoint?.x ?? annotation.x) != null && (annotation.tickPoint?.y ?? annotation.y) != null);
  return Number((candidates.find(annotation => !annotation.locationNeedsReview) || candidates[0])?.page) || null;
}

export function printedAnchorFor(annotation, printed) {
  const key = questionAnchorKey(annotation.questionNumber);
  const exact = printed.index.get(key) || [];
  const parent = /\.[a-z]$/.test(key) ? printed.index.get(key.replace(/\.[a-z]$/, '')) || [] : [];
  const candidates = exact.length ? exact : parent;
  const page = Number(annotation.page);
  const hinted = candidates.filter(candidate => candidate.page === page);
  // Repeated headers on different pages need page evidence, not an arbitrary first hit.
  const candidate = hinted.length === 1 ? hinted[0] : candidates.length === 1 ? candidates[0] : null;
  if (candidate) {
    // A section total at the bottom of the last question is not that item's mark bracket.
    const bracket = candidate.bracket && (!annotation.questionMax || !exact.length || candidate.bracket.value === Number(annotation.questionMax)) ? candidate.bracket : null;
    return { ...candidate, anchor: bracket || candidate, kind: bracket ? 'mark_bracket' : 'question_number', parentFallback: !exact.length };
  }
  const box = annotation.evidenceBox;
  if (!box || !Number.isInteger(page)) return null;
  const nearby = printed.brackets.filter(bracket => bracket.page === page && bracket.cy >= box.y - .012
    && bracket.cy <= box.y + box.height + .012 && (!annotation.questionMax || bracket.value === Number(annotation.questionMax)));
  return nearby.length === 1 ? { ...nearby[0], anchor: nearby[0], kind: 'mark_bracket', parentFallback: false } : null;
}

/** A scored item with missing provider annotations still needs a visible score indicator. */
export function ensureGradedItemAnnotations(annotations = [], results = []) {
  const regular = results.filter(result => result?.questionNumber && !isRubricTask(result) && Number.isFinite(Number(result.marksAwarded)) && Number(result.maxMarks) > 0);
  if (!regular.length) return annotations;
  const byKey = new Map(regular.map(result => [questionAnchorKey(result.questionNumber), result]));
  const output = annotations.filter(Boolean).map(annotation => {
    const result = byKey.get(questionAnchorKey(annotation.questionNumber));
    return result ? { ...annotation, questionMax: Number(result.maxMarks) } : annotation;
  });
  for (const result of regular) {
    if (output.some(annotation => questionAnchorKey(annotation.questionNumber) === questionAnchorKey(result.questionNumber))) continue;
    const score = Number(result.marksAwarded); const maximum = Number(result.maxMarks);
    const statuses = score <= 0 ? [false] : score < maximum ? [true, false] : [true];
    statuses.forEach((awarded, index) => output.push({ questionNumber: result.questionNumber, markNumber: index + 1,
      questionMax: maximum, page: Math.max(1, Number(result.evidencePage) || 1), evidenceText: result.learnerAnswer || '',
      evidenceBox: result.answerBox || null, x: null, y: null, tickPoint: null, needsPlacement: true,
      type: awarded ? 'confirmed' : 'lost', awarded, status: awarded ? 'awarded' : 'not_awarded',
      sourceCoordinate: 'graded_item_indicator', scoreLabel: index === 0 ? `${score}/${maximum}` : '', summaryIndicator: true }));
  }
  return output;
}

/** Last resort is a labelled review strip on a script page, never a fabricated answer location. */
export function withReviewStripFallback(annotations, pageCount, results) {
  const evaluated = new Set((results || []).filter(result => !isRubricTask(result)).map(result => questionAnchorKey(result.questionNumber)));
  const slots = new Map();
  const baseX = Math.max(1.035, ...annotations.filter(annotation => !annotation.needsPlacement && annotation.sourceCoordinate !== 'page_review_strip')
    .map(annotation => Number(annotation.tickPoint?.x ?? annotation.x) + .13).filter(Number.isFinite));
  return annotations.map(annotation => {
    const x = annotation.tickPoint?.x ?? annotation.x; const y = annotation.tickPoint?.y ?? annotation.y;
    const page = Number(annotation.page);
    if (!annotation.needsPlacement && x != null && y != null && Number.isFinite(Number(x)) && Number.isFinite(Number(y))
      && page >= 1 && page <= pageCount) return annotation;
    if (!evaluated.has(questionAnchorKey(annotation.questionNumber))) return annotation;
    const targetPage = Math.min(pageCount, Math.max(1, Number.isInteger(page) ? page : 1));
    const slot = slots.get(targetPage) || 0; slots.set(targetPage, slot + 1);
    const point = { x: baseX + Math.floor(slot / 34) * .22, y: .065 + (slot % 34) * .026 };
    return { ...annotation, ...point, tickPoint: point, page: targetPage, needsPlacement: false,
      sourceCoordinate: 'page_review_strip', locationNeedsReview: true,
      reviewLabel: `Q${annotation.questionNumber}${annotation.scoreLabel ? ` ${annotation.scoreLabel}` : ` #${annotation.markNumber || 1}`}` };
  });
}
